import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { ReactNode } from "react";

import {
	MechanicalResultsContent,
	useHeatMechanicalResult,
} from "@/pages/lab/chemistries/components/MechanicalResultsContent";
import {
	MechanicalCurvesChart,
	useHeatMechanicalCurves,
} from "@/pages/mill/schedule/components/mechanical-results/MechanicalCurvesChart";
import { SoftChip } from "@/pages/mill/schedule/components/mechanical-results/cells";
import { CampaignResponse } from "@/pages/mill/schedule/models/CampaignReponse";
import { MILLS, MillId } from "@/setup/mill-config/mill-config";
import { pageBgColor } from "@/utils/theme";
import {
	ChemistryResultsContent,
	hasChemistryValues,
	useChemistryResults,
} from "./ChemistryResultsContent";

type Props = {
	heat: CampaignResponse | null;
	millId: MillId | null;
	canEdit?: boolean;
};

const SectionTitle = ({ children }: { children: string }) => (
	<Typography variant="subtitle2" fontWeight={600} color="text.secondary">
		{children}
	</Typography>
);

const Card = ({
	children,
	grow = false,
}: {
	children: ReactNode;
	grow?: boolean;
}) => (
	<Paper
		variant="outlined"
		sx={{
			p: 1.5,
			minWidth: 0,
			flexShrink: 0,
			display: "flex",
			flexDirection: "column",
			...(grow ? { flex: 1 } : {}),
		}}
	>
		{children}
	</Paper>
);

export const HeatDetailsPanel = ({ heat, millId, canEdit = false }: Props) => {
	const { data: mechanical } = useHeatMechanicalResult({
		heatId: heat?.HeatId,
	});
	const { data: curves } = useHeatMechanicalCurves(heat?.HeatId);
	const {
		data: chemistryRows,
		isInitialLoading: isLoadingChemistry,
		isError: isChemistryError,
	} = useChemistryResults(heat?.HeatId ?? null);

	if (!heat) {
		return (
			<Stack
				alignItems="center"
				justifyContent="center"
				sx={{ height: "100%", p: 3 }}
			>
				<Typography variant="body2" color="text.secondary">
					Select a heat to see its chemistry and mechanical tests
				</Typography>
			</Stack>
		);
	}

	const hasChemistry = hasChemistryValues(chemistryRows?.[0] ?? null);
	const hasCurves = !!heat.HeatId && (curves?.length ?? 0) > 0;

	return (
		<Stack
			spacing={1.5}
			sx={{
				height: "100%",
				minHeight: 0,
				overflow: "auto",
				py: 1.5,
				bgcolor: pageBgColor,
			}}
		>
			<Paper variant="outlined" sx={{ px: 2, py: 1.25 }}>
				<Stack direction="row" spacing={1} alignItems="center">
					<Stack
						direction="row"
						spacing={1.5}
						alignItems="baseline"
						flexWrap="wrap"
						useFlexGap
						sx={{ flex: 1, minWidth: 0 }}
					>
						<Typography variant="h6" fontWeight={600}>
							{`Heat ${heat.HeatName ?? "-"}`}
						</Typography>
						{millId && <SoftChip label={MILLS[millId].name} />}
						<Typography variant="body2" color="text.secondary">
							Grade{" "}
							<Box
								component="span"
								sx={{ color: "text.primary", fontWeight: 600 }}
							>
								{heat.GradeName ?? "-"}
							</Box>
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Section{" "}
							<Box
								component="span"
								sx={{ color: "text.primary", fontWeight: 600 }}
							>
								{heat.SectionName ?? "-"}
							</Box>
						</Typography>
					</Stack>
					{!isLoadingChemistry && !isChemistryError && !hasChemistry && (
						<SoftChip
							icon={<ScienceOutlinedIcon />}
							label="Chemistry: not loaded"
							dashed
						/>
					)}
				</Stack>
			</Paper>

			{(isLoadingChemistry || isChemistryError || hasChemistry) && (
				<Card>
					<Stack spacing={1}>
						<SectionTitle>Chemistry</SectionTitle>
						<ChemistryResultsContent heat={heat} />
					</Stack>
				</Card>
			)}

			<Stack
				direction={{ xs: "column", lg: "row" }}
				spacing={1.5}
				sx={{ flex: "1 0 auto" }}
			>
				<Card grow>
					<MechanicalResultsContent
						heat={heat}
						millId={millId ?? undefined}
						canEdit={canEdit}
						showSeqColors={hasCurves}
						compact
					/>
				</Card>
				{hasCurves && (
					<Card grow>
						<MechanicalCurvesChart
							heatId={heat.HeatId!}
							heatName={heat.HeatName}
							results={mechanical?.results ?? []}
							fill
						/>
					</Card>
				)}
			</Stack>
		</Stack>
	);
};
